import DataQuery from 'data/DataQuery';
import { Customer_Contacts } from 'data/endpoints';
import ContactDTO from 'features/customer/dtos/ContactDTO';
import CreateRequirementResultSubDialog from 'features/requirements/components/CreateRequirementResultSubDialog';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getCustomerContactsParams } from 'features/shared/helpers/queryParams';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import { mobilePhoneFormValidations } from './mobilePhoneFormValidation';
import { mobilePhoneFormInitValues } from './mobilePhoneFormInitValues';
import PhoneInput from 'features/shared/forms/components/phoneInput/PhoneInput';
import IRepository from 'data/IRepository';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import PostResponse from 'data/PostResponse';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { useTranslation } from 'react-i18next';

interface Props extends BaseRequirementFormProps {
}

const MobilePhoneFormSection = (props?: Props) => {
  //translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);
  const repository = useInjection<IRepository<ContactDTO>>(IocTypes.Repository);

  // router location
  const location = useLocation<ContactDTO>();

  // props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("mobilePhoneFormSection.sendRequirement");
  const successMessage: string = useOutsideOfRequirements ? tLabels("mobilePhoneFormSection.successMessageOutsideRequirement") : tLabels("mobilePhoneFormSection.successMessage");
  const errorMessage: string | undefined = useOutsideOfRequirements ? tLabels("mobilePhoneFormSection.errorMessage") : undefined;

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // refs
  const phonePrefixEl = useRef<HTMLSelectElement>(null);

  // form hook
  const { values, setFormValue, errors, bindField, bindFieldNew, bindUncontrolledField, valideteForm, setValidationRule } = useForm({
    initialValues: mobilePhoneFormInitValues(),
    initialValidations: mobilePhoneFormValidations(phonePrefixEl)
  });

  // side effects
  useEffect(() => {
    setOriginalMobile();
  }, [])

  // helpers 
  const setOriginalMobile = async () => {
    // load contact from location (router)
    if (location.state) {
      setFormValue("DESC_TMMOBILPUVODNI", location.state.value); 
    }
    // load contact from API
    else {
      let query: DataQuery = {url: Customer_Contacts, routeParams: getRouteParams("customerId", customer.customerId!), params: getCustomerContactsParams("Mobile", 1)};
      let contacts = await repository.getPagedResult(query);
      if (contacts.count === 1) {
        setFormValue("DESC_TMMOBILPUVODNI", contacts.result[0].value); 
      }
    }
  }

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let valuesCopy = {...values};
      valuesCopy["DESC_TMMOBIL"] = `${values["phonePrefix"]}${values["DESC_TMMOBIL"]}`.replace(/\s/g, "");

      let result = await createService.createRequirement("ZMENA_MOBIL_TELEFONU", valuesCopy);
      openResultDialogHandler(result);
    }
  };

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: successMessage,
      errorMessage: errorMessage,
      actionBtn: { title: tLabels("mobilePhoneFormSection.identificationData"), route: "/customer" },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("mobilePhoneFormSection.changeMobileNumber"), true); 
  }

  // render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard title={tLabels("mobilePhoneFormSection.cardTitle")} bodyClassName="pt-4 px-4">
        <div className="row no-gutters mx-n2">
          <div className="col-sm-6 px-2">
            <FormGroup labelText={tLabels("mobilePhoneFormSection.originalMobileNumber")}>
              <input className="form-control" disabled defaultValue={values["DESC_TMMOBILPUVODNI"] ?? ""} />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup labelText={tLabels("mobilePhoneFormSection.newMobileNumber")} inputId="DESC_TMMOBIL" validationError={errors["DESC_TMMOBIL"]}>
              <PhoneInput
                ref={phonePrefixEl}
                id="DESC_TMMOBIL"
                {...bindFieldNew("DESC_TMMOBIL")}
                onSelectChange={(e) => setFormValue("phonePrefix", e.target.value)}
                placelhoder={tLabels("mobilePhoneFormSection.setNewMobileNumber")}
              />
            </FormGroup>
          </div>
        </div>       
      </PageCard>

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <button type="submit" className="btn btn-primary px-3 py-2">{sendButtonTitle}</button>
    </form>
  )
}

export default MobilePhoneFormSection;
